export function getPhone(
  phone: string,
  position: 'homePhone' | 'cellPhone' | 'businessPhone',
) {
  const select = {
    homePhone: 0,
    cellPhone: 1,
    businessPhone: 2,
  };

  const splited = (phone && phone.split(';')) || null;

  if (!splited) {
    return '';
  }

  return splited[select[position]];
}

export async function addDataCache(
  cacheName: string,
  data: object,
  url: string,
  expirationTimeSeconds?: number,
) {
  try {
    const newData = new Response(JSON.stringify(data), {
      headers: { 'Cache-Control': `max-age=${expirationTimeSeconds}` },
    });

    if ('caches' in window) {
      const cache = await caches.open(cacheName);
      await cache.put(url, newData);
    }
  } catch (error) {
    console.error('Erro ao adicionar dados ao cache:', error);
  }
}

export function cleanCache(key?: string) {
  if (key) {
    caches.delete(key);
  } else {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
}

export async function getDataCache(cacheName: string, url: string) {
  try {
    if ('caches' in window) {
      const cache = await caches.open(cacheName);
      const cachedResponse = await cache.match(url);

      if (cachedResponse) {
        const cacheExpiration = cachedResponse.headers.get('Expires');
        if (cacheExpiration && new Date(cacheExpiration) < new Date()) {
          await cache.delete(url);
          return null;
        } else {
          const data = await cachedResponse.json();
          return data;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Erro ao obter dados do cache:', error);
    return null;
  }
}

export function formatDate(date?: string, timesTamp: string = 'pt-br') {
  if (!date) return '';
  return new Date(
    !date.includes(':') ? date + 'T00:00:00' : date,
  ).toLocaleDateString(timesTamp);
}

export function formatDateHour(dateTimeString?: string) {
  if (!dateTimeString) return '';

  const dateTime = new Date(
    !dateTimeString.includes(':')
      ? dateTimeString + 'T00:00:00'
      : dateTimeString,
  );

  return dateTime
    .toLocaleString('pt-BR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
    .replace(',', '');
}

export function formatMonthDay(dateTimeString: string) {
  if (!dateTimeString) return '';

  const dateTime = new Date(
    !dateTimeString.includes(':')
      ? dateTimeString + 'T00:00:00'
      : dateTimeString,
  );

  return dateTime
    .toLocaleString('pt-BR', {
      month: 'long',
      year: 'numeric',
    })
    .replace(' de ', '/');
}

export function formatCpf(cpf?: string | number) {
  if (!cpf) return;

  if (typeof cpf !== 'number' && cpf.length !== 11) return cpf;

  let newCpf = cpf;
  newCpf = newCpf.toString();
  newCpf = newCpf.replace(/[^\d]/g, '');

  return newCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function formatCurrency(
  value: number | string,
  currency: string = 'BRL',
) {
  const newValue = typeof value === 'string' ? parseFloat(value) : value;

  return new Intl.NumberFormat('pt-br', {
    style: 'currency',
    currency: currency,
  }).format(newValue);
}

export function formatDecimal(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercent(value: number) {
  return `${formatDecimal(value)}%`;
}

export function toCaptalize(text: string) {
  return text.trim().charAt(0).toUpperCase() + text.trim().slice(1);
}

export function toUpperCase(text: string) {
  return text.toLocaleUpperCase('pt-BR');
}

export function cpfIsValid(cpfString: string) {
  const cpfClean = cpfString.replace(/[^\d]/g, '');

  if (cpfClean.length !== 11 || /^(\d)\1{10}$/.test(cpfClean)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpfClean.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== parseInt(cpfClean.substring(9, 10))) {
    return false;
  }

  sum = 0;

  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpfClean.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== parseInt(cpfClean.substring(10, 11))) {
    return false;
  }

  return true;
}

export function emailIsValid(emailString: string) {
  if (emailString) {
    return (
      emailString !== '' &&
      emailString !== null &&
      /[a-zA-Z0-9]+[.]?([a-zA-Z0-9]+)?[@][a-z]{3,25}([.][a-z]{2,5})?[.][a-z]{2,5}/g.test(
        emailString,
      )
    );
  }
}

export function cleanTrailingEmptyParagraph(html: string = ''): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  let last = div.lastElementChild;

  while (
    last &&
    last.tagName === 'P' &&
    last.innerHTML.replace(/&nbsp;|<br\s*\/?>|\s+/gi, '').trim() === ''
  ) {
    div.removeChild(last);
    last = div.lastElementChild;
  }

  return div.innerHTML;
}

export function numberToString(value: string) {
  if (!value) {
    return '';
  }

  if (!value.includes('.') && !value.includes(',')) {
    value = parseInt(value).toFixed(2);
  } else {
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1].length === 1) {
        value = `${value}0`;
      }
    } else if (value.includes(',')) {
      const parts = value.split(',');
      if (parts[1].length === 1) {
        value = `${value}0`;
      }
    }
  }

  const numericValue = value.replace(/\D/g, '');

  const floatValue = (parseFloat(numericValue) / 100).toFixed(2);

  return floatValue.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function convertStringToDate(
  dateString: string,
  includeTime: boolean = false,
  format: string = 'date',
): string | null {
  if (!dateString || typeof dateString !== 'string') return null;

  const normalizedDateString = dateString.replace(' ', 'T');

  let date;
  if (!includeTime && format !== 'time') {
    date = new Date(normalizedDateString.split('T')[0] + 'T00:00:00');
  } else {
    date = new Date(normalizedDateString);
  }

  if (!isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    let formattedDate = '';
    if (format === 'date') {
      formattedDate = `${day}/${month}/${year}`;
    } else if (format === 'monthYear') {
      formattedDate = `${month}/${year}`;
    } else if (format === 'month') {
      formattedDate = `${month}`;
    } else if (format === 'year') {
      formattedDate = `${year}`;
    } else if (format === 'monthYearLong') {
      const monthName = date.toLocaleString('pt-BR', { month: 'long' });
      formattedDate = `${toCamelCase(monthName)} de ${year}`;
    } else if (format === 'time') {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    if (includeTime && format !== 'time') {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      formattedDate += ` - ${hours}:${minutes}`;
    }

    return formattedDate;
  }

  return null;
}

function toCamelCase(str: string) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match: string, index: number) =>
      index !== 0 ? match.toLowerCase() : match.toUpperCase(),
    )
    .replace(/\s+/g, '');
}